import React, { useMemo, useState } from "react";
import type { CardContactsAttachment, ScannedCardContact } from "../types";
import {
  explainSaveError,
  markCardsApproved,
  type CardSaveRow,
} from "../../../../features/card-to-crm/chatCardService";
import { saveAllCards } from "../../../../features/card-to-crm/receptionistCardService";

interface ScannedCardsCardProps {
  data: CardContactsAttachment;
  customerId: string;
  idToken: string;
}

type Phase = "review" | "saving" | "done" | "error";

const initials = (name: string | null) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

// Same inline color-mix reason as CallsListCard: `bg-theme-secondary/20` can't
// alpha-blend a bare var() colour and silently renders with no background.
const avatarTint = (i: number): React.CSSProperties => {
  const variants: React.CSSProperties[] = [
    { background: "color-mix(in srgb, var(--color-primary) 16%, transparent)", color: "var(--color-primary)" },
    { background: "color-mix(in srgb, var(--color-secondary) 18%, transparent)", color: "var(--color-primary)" },
    {
      background: "color-mix(in srgb, var(--color-accent-warm) 24%, transparent)",
      color: "color-mix(in srgb, var(--color-accent-warm) 55%, var(--color-text-primary) 45%)",
    },
  ];
  return variants[i % variants.length];
};

const mutedChip: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-text-primary) 8%, transparent)",
};
const warnChip: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-accent-warm) 22%, transparent)",
  color: "color-mix(in srgb, var(--color-accent-warm) 55%, var(--color-text-primary) 45%)",
};
const okChip: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-primary) 12%, transparent)",
  color: "var(--color-primary)",
};
const badChip: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-danger) 16%, var(--color-surface))",
  color: "var(--color-danger)",
};

const subtitleOf = (c: ScannedCardContact) =>
  [c.title, c.company].filter(Boolean).join(" · ");

/**
 * SGL-346: the scanned-cards review card.
 *
 * The cards arrive already drafted, but NOTHING is in the CRM and nothing has been
 * emailed — this card is the confirmation step, and the save call below is the only
 * thing that writes. The draft text shown here is handed straight back to the save
 * endpoint, so what goes out is exactly what was read on screen; the card never
 * re-drafts and never edits.
 *
 * `proposedAction` comes from what the owner actually asked for in chat ("just save
 * them, no email" -> save_only), so the button has to follow it rather than always
 * offering to send.
 *
 * Outcomes are tracked per contact, keyed by the contact's position in the ORIGINAL
 * list, because a retry submits only the failures and the endpoint indexes its rows
 * against whatever array it was given. Retrying anything already saved would create
 * a duplicate in the CRM and email the person a second time.
 */
/** Outcomes for a card the owner already approved, rebuilt from the stored turn.
 *
 * A live card keeps these in component state, which a reload throws away. Starting
 * every restored card back at "review" is what made a settled batch render as
 * "nothing has been sent" with the button live again — clicking it sends the same
 * people a second email. Seeding from what was persisted makes `nextAttempt` empty,
 * which is what removes the button.
 */
const restoredResults = (data: CardContactsAttachment): Record<number, CardSaveRow> => {
  if (!data.saved) return {};
  const out: Record<number, CardSaveRow> = {};
  data.contacts.forEach((c, i) => {
    // A contact the save rejected stays absent, so it is offered as a retry rather
    // than shown as settled.
    if (c.saved === false) return;
    out[i] = {
      index: i,
      ok: true,
      sent: c.sent === true,
      send_error: null,
      error: null,
    };
  });
  return out;
};

export const ScannedCardsCard: React.FC<ScannedCardsCardProps> = ({
  data,
  customerId,
  idToken,
}) => {
  const { contacts, captureContext, tags, proposedAction } = data;
  const [openIndex, setOpenIndex] = useState<number | null>(contacts.length === 1 ? 0 : null);
  const [phase, setPhase] = useState<Phase>(data.saved ? "done" : "review");
  const [error, setError] = useState<string>("");
  const [results, setResults] = useState<Record<number, CardSaveRow>>(() =>
    restoredResults(data)
  );
  // Owner's edits to the drafted text, by contact index. What is in here is what
  // gets sent — the save call reads from `edited`, never from `data`.
  const [edited, setEdited] = useState<Record<number, { subject: string; body: string }>>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  /** The draft as it currently reads on screen. */
  const draftOf = (c: ScannedCardContact, i: number) => edited[i] ?? c.draft;
  const editDraft = (i: number, c: ScannedCardContact, field: "subject" | "body", value: string) =>
    setEdited((prev) => {
      const current = prev[i] ?? c.draft;
      if (!current) return prev;
      return { ...prev, [i]: { ...current, [field]: value } };
    });

  const willSend = proposedAction === "save_and_send";
  const sendableCount = useMemo(
    () => contacts.filter((c) => c.sendable && c.draft).length,
    [contacts]
  );

  const allIndices = useMemo(() => contacts.map((_, i) => i), [contacts]);
  const failedIndices = useMemo(
    () => allIndices.filter((i) => results[i] && !results[i].ok),
    [allIndices, results]
  );
  const savedIndices = useMemo(
    () => allIndices.filter((i) => results[i]?.ok),
    [allIndices, results]
  );
  const settled = savedIndices.length + failedIndices.length > 0;

  // The failures if there are any, otherwise everything not yet attempted. Never a
  // contact that already saved.
  const nextAttempt = failedIndices.length
    ? failedIndices
    : allIndices.filter((i) => !results[i]);

  const savedCount = savedIndices.length;
  const sentCount = useMemo(
    () => allIndices.filter((i) => results[i]?.sent === true).length,
    [allIndices, results]
  );
  const isRetry = settled && failedIndices.length > 0;

  const buttonLabel = () => {
    if (phase === "saving") {
      if (isRetry) return "Retrying…";
      return willSend ? "Saving & sending…" : "Saving…";
    }
    if (isRetry) return `Retry the ${failedIndices.length} that failed`;
    if (!willSend) return `Save ${nextAttempt.length}`;
    if (sendableCount === contacts.length) return `Save & send ${contacts.length}`;
    // Saying "Save & send 3" when only 2 can be emailed overstates what happens.
    return `Save ${contacts.length} & send ${sendableCount}`;
  };

  const submit = async (indices: number[]) => {
    if (!indices.length) return;
    setPhase("saving");
    setError("");
    const picked = indices.map((i) => ({ ...contacts[i], draft: draftOf(contacts[i], i) }));
    try {
      // Straight to API Gateway — the CRM save is the Lambda's, and routing it via
      // the chat service would make the agent call the Lambda (SGL-346 call
      // ownership). Both guards below used to live in that proxy and have to travel
      // with the call.
      const outcomes = await saveAllCards(
        customerId,
        picked.map((c, j) => ({
          id: String(indices[j]),
          name: c.name,
          title: c.title,
          company: c.company,
          email: c.email,
          // A card printing only a mobile extracts into `mobile`, and GHL has one
          // phone field. The save-all handler reads `phone` and does not fall back,
          // so forwarding the card verbatim asks it to create a contact with no email
          // and no phone — a 400 the owner sees beside a card with a working number
          // on it. Verified live 2026-09-25.
          phone: (c.phone || "").trim() || (c.mobile || "").trim() || null,
          mobile: c.mobile,
          website: c.website,
        })),
        captureContext,
        idToken,
        willSend,
        // Only a contact the drafting step cleared may carry an email into the send,
        // so an unsendable row cannot be mailed by asking for it here.
        picked.map((c) => (c.draft && c.sendable ? c.draft : null)),
        tags
      );

      // The rows are indexed against what was just submitted, not against the card
      // list — map them home before merging, or a retry writes its outcome onto the
      // wrong contact's row.
      const rows: CardSaveRow[] = outcomes.map((o, j) => ({
        index: indices[j],
        ok: o.ok,
        sent: o.sent ?? null,
        send_error: o.sendError ?? null,
        error: o.ok ? null : explainSaveError(o.error),
      }));
      setResults((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          next[r.index] = r;
        });
        return next;
      });
      setPhase("done");

      // Second call, not a proxy hop: settle the stored turn so a reload cannot
      // re-offer the send. Deliberately after the phase flips — the contacts are in
      // the CRM whatever this returns, and the owner must not be told otherwise.
      if (data.threadId && data.cardMessageSk) {
        const merged: Record<number, CardSaveRow> = { ...results };
        rows.forEach((r) => {
          merged[r.index] = r;
        });
        void markCardsApproved({
          customerId,
          threadId: data.threadId,
          cardMessageSk: data.cardMessageSk,
          contacts: contacts.map((c, i) => ({
            ...c,
            draft: draftOf(c, i),
            saved: merged[i]?.ok === true,
            sent: merged[i]?.sent === true,
            reason: merged[i]?.error ?? null,
          })),
        });
      }
    } catch (e) {
      // Never render "saved" on a failure — this card is the owner's only record
      // of what actually reached their CRM.
      setError(
        e instanceof Error ? e.message : "Couldn't save these contacts. Try again in a moment."
      );
      setPhase("error");
    }
  };

  const rowStatus = (c: ScannedCardContact, i: number) => {
    const o = results[i];
    if (o) {
      if (!o.ok) return { label: "Failed", style: badChip };
      if (o.sent === true) return { label: "Sent", style: okChip };
      if (o.send_error) return { label: "Saved, not sent", style: warnChip };
      return { label: "Saved", style: okChip };
    }
    if (!c.sendable) return { label: "No email", style: warnChip };
    if (c.draft) return { label: "Draft ready", style: mutedChip };
    return { label: "No draft", style: warnChip };
  };

  /** Why a row reads the way it does, shown whether or not the row is expanded —
   *  a bare "Failed" chip with the reason hidden behind a click is not an answer. */
  const rowReason = (c: ScannedCardContact, i: number) => {
    const o = results[i];
    if (o && !o.ok) return { text: o.error || "Couldn't be saved.", bad: true };
    if (o?.send_error) return { text: `Saved, but the email didn't send: ${o.send_error}`, bad: true };
    if (o) return null;
    if (!c.sendable && c.reason) {
      // The reasons arrive lower-case from the server ("no email address on the
      // card"). With a draft in hand the blocker is worth naming as such; without
      // one the reason stands on its own.
      return {
        text: c.draft
          ? `Won't be emailed — ${c.reason}`
          : c.reason.charAt(0).toUpperCase() + c.reason.slice(1),
        bad: false,
      };
    }
    return null;
  };

  return (
    <div className="my-1 w-full max-w-[460px] overflow-hidden rounded-2xl border border-theme-border bg-theme-surface">
      <div className="flex items-center justify-between gap-2 border-b border-theme-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl"
            style={
              settled && failedIndices.length
                ? { background: "color-mix(in srgb, var(--color-danger) 14%, transparent)" }
                : { background: "color-mix(in srgb, var(--color-secondary) 20%, transparent)" }
            }
          >
            <span
              className="material-icons-outlined text-[18px] text-theme-fg-muted"
              style={settled && failedIndices.length ? { color: "var(--color-danger)" } : undefined}
            >
              {settled ? (failedIndices.length ? "error_outline" : "check_circle") : "badge"}
            </span>
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-bold text-theme-fg">
              {settled
                ? `${savedCount} of ${contacts.length} saved${
                    willSend ? ` · ${sentCount} sent` : ""
                  }`
                : `${contacts.length} card${contacts.length === 1 ? "" : "s"} scanned`}
            </p>
            <p className="truncate text-[11px] text-theme-fg-muted">
              {settled
                ? failedIndices.length
                  ? "Some contacts weren't saved"
                  : "Done"
                : captureContext || "Review before saving"}
            </p>
          </div>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-shrink-0 flex-wrap justify-end gap-1">
            {tags.map((t) => (
              <span
                key={t}
                className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-theme-fg-muted"
                style={mutedChip}
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {contacts.length === 0 && (
        <p className="px-4 py-5 text-center text-[12px] text-theme-fg-muted">
          No contacts were read from those photos. Try a clearer, straight-on shot.
        </p>
      )}

      <ul className="divide-y divide-theme-border">
        {contacts.map((c, i) => {
          const isOpen = openIndex === i;
          const status = rowStatus(c, i);
          const reason = rowReason(c, i);
          return (
            <li key={`${c.email ?? c.name ?? "card"}-${i}`}>
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-theme-background"
              >
                <span
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
                  style={avatarTint(i)}
                >
                  {initials(c.name)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-theme-fg">
                    {c.name || "Unnamed contact"}
                  </span>
                  <span className="block truncate text-[11px] text-theme-fg-muted">
                    {subtitleOf(c) || c.email || "No details read"}
                  </span>
                </span>
                <span
                  className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold text-theme-fg-muted"
                  style={status.style}
                >
                  {status.label}
                </span>
                <span className="material-icons-outlined flex-shrink-0 text-[18px] text-theme-fg-muted">
                  {isOpen ? "expand_less" : "expand_more"}
                </span>
              </button>

              {reason && (
                <p
                  className="px-4 pb-2.5 pl-[58px] text-[11px]"
                  style={reason.bad ? { color: "var(--color-danger)" } : undefined}
                >
                  <span className={reason.bad ? undefined : "text-theme-fg-muted"}>
                    {reason.text}
                  </span>
                </p>
              )}

              {isOpen && (
                <div className="bg-theme-background px-4 pb-3.5 pt-1">
                  <dl className="mb-2.5 grid grid-cols-[64px_1fr] gap-x-2 gap-y-1 text-[11px]">
                    {([
                      ["Email", c.email],
                      ["Phone", c.phone || c.mobile],
                      ["Website", c.website],
                    ] as const).map(([label, value]) =>
                      value ? (
                        <React.Fragment key={label}>
                          <dt className="text-theme-fg-muted">{label}</dt>
                          <dd className="truncate text-theme-fg">{value}</dd>
                        </React.Fragment>
                      ) : null
                    )}
                  </dl>

                  {draftOf(c, i) ? (
                    <div className="rounded-xl border border-theme-border bg-theme-surface p-3">
                      {editingIndex === i ? (
                        <>
                          <input
                            type="text"
                            aria-label="Email subject"
                            value={draftOf(c, i)!.subject}
                            onChange={(e) => editDraft(i, c, "subject", e.target.value)}
                            className="mb-1.5 w-full rounded-lg border border-theme-border bg-theme-background px-2 py-1 text-[12px] font-bold text-theme-fg outline-none focus:border-theme-primary"
                          />
                          <textarea
                            aria-label="Email body"
                            value={draftOf(c, i)!.body}
                            onChange={(e) => editDraft(i, c, "body", e.target.value)}
                            rows={7}
                            className="w-full resize-none rounded-lg border border-theme-border bg-theme-background px-2 py-1 text-[12px] leading-relaxed text-theme-fg outline-none focus:border-theme-primary"
                          />
                        </>
                      ) : (
                        <>
                          <p className="mb-1.5 text-[12px] font-bold text-theme-fg">
                            {draftOf(c, i)!.subject}
                          </p>
                          {/* Body in full-contrast text, not muted: this is the email
                              the owner is about to put their name on. */}
                          <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-theme-fg">
                            {draftOf(c, i)!.body}
                          </p>
                        </>
                      )}
                      {!settled && (
                        <button
                          type="button"
                          onClick={() => setEditingIndex(editingIndex === i ? null : i)}
                          className="mt-2 text-[11px] font-semibold"
                          style={{ color: "var(--color-primary)" }}
                        >
                          {editingIndex === i ? "Done editing" : "Edit this email"}
                        </button>
                      )}
                    </div>
                  ) : (
                    // The specific reason already sits on the row above, visible
                    // whether or not this is open — don't print it twice.
                    <p className="text-[11px] text-theme-fg-muted">
                      No intro email was drafted for this contact.
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {contacts.length > 0 && (
        <div className="border-t border-theme-border px-4 py-3">
          {phase === "error" && (
            <p className="mb-2 text-[11px]" style={{ color: "var(--color-danger)" }}>
              {error}
            </p>
          )}

          {proposedAction === "draft_only" && !settled ? (
            <p className="text-[11px] text-theme-fg-muted">
              Drafts only — tell me when you want these saved.
            </p>
          ) : (
            <>
              <p className="mb-2 text-[11px] text-theme-fg-muted">
                {!settled
                  ? /* Always visible, not just when there's no capture context to show
                       in the header: this promise is the whole reason the card exists. */
                    willSend
                    ? "Nothing is saved yet and no email has been sent."
                    : "Nothing is saved yet."
                  : failedIndices.length
                    ? `${savedCount === 0 ? "Nothing was saved" : `${savedCount} saved`} — retrying only touches the ${failedIndices.length} that failed, so no one is contacted twice.`
                    : tags.length
                      ? `All ${contacts.length} in your CRM, tagged ${tags.join(", ")}.`
                      : `All ${contacts.length} are in your CRM.`}
              </p>
              {nextAttempt.length > 0 && (
                <button
                  type="button"
                  onClick={() => submit(nextAttempt)}
                  disabled={phase === "saving"}
                  className="w-full rounded-xl bg-theme-primary px-4 py-2 text-[13px] font-semibold text-white transition-opacity disabled:opacity-60"
                >
                  {buttonLabel()}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ScannedCardsCard;
